/**
 * « Lien de paiement en ligne » — shows the public /payer link of an
 * invoice so the operator can copy it and send it to the customer.
 * Server: GET /api/pispi/invoices/:id/payment-link.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { AlertCircle, Check, Copy, Link2, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/api/client';
import { useDialogA11y } from '@/hooks/useDialogA11y';
import { usePispiPaymentLink } from '@/api/queries/pispi';

interface PaymentLinkDialogProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: { id: string; number: string };
}

function linkErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    // The PI-SPI routes are not mounted: the server has no PISPI_ENCRYPTION_KEY.
    if (error.status === 404) return 'Le paiement en ligne n’est pas activé sur ce serveur.';
    return error.message;
  }
  return 'Impossible d’obtenir le lien de paiement.';
}

function formatExpiry(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }).format(new Date(iso));
}

export function PaymentLinkDialog({ isOpen, onClose, invoice }: PaymentLinkDialogProps) {
  const dialogRef = useDialogA11y({ open: isOpen, onClose });
  const link = usePispiPaymentLink(invoice.id, { enabled: isOpen });
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const copy = async () => {
    if (!link.data) return;
    try {
      await navigator.clipboard.writeText(link.data.url);
      setCopied(true);
      toast.success('Lien copié');
    } catch {
      toast.error('Copie impossible : sélectionnez le lien et copiez-le manuellement.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="payment-link-dialog-title"
        className="relative mx-4 w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl"
      >
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-lagune-50">
            <Link2 className="h-5 w-5 text-brand-lagune-700" />
          </div>
          <div>
            <h3 id="payment-link-dialog-title" className="text-lg font-semibold text-slate-900">
              Lien de paiement en ligne
            </h3>
            <p className="text-sm text-slate-500">Facture {invoice.number}</p>
          </div>
        </div>

        {link.isLoading ? (
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            Préparation du lien…
          </div>
        ) : link.isError ? (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700" role="alert">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{linkErrorMessage(link.error)}</span>
          </div>
        ) : link.data ? (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Envoyez ce lien à votre client : il pourra régler le solde de la facture par PI-SPI depuis
              l’application de sa banque ou de son portefeuille électronique.
            </p>
            <div>
              <Label htmlFor="payment-link-url" className="mb-2 block">
                Lien
              </Label>
              <div className="flex gap-2">
                <Input
                  id="payment-link-url"
                  readOnly
                  value={link.data.url}
                  onFocus={(e) => e.currentTarget.select()}
                  className="font-mono text-xs"
                />
                <Button type="button" onClick={copy} className="shrink-0">
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copied ? 'Copié' : 'Copier'}
                </Button>
              </div>
            </div>
            <p className="text-xs text-slate-500">Valable jusqu’au {formatExpiry(link.data.expiresAt)}.</p>
          </div>
        ) : null}

        <div className="mt-6 flex justify-end">
          <Button variant="outline" onClick={onClose}>
            Fermer
          </Button>
        </div>
      </div>
    </div>
  );
}
